import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Star, Quote } from "lucide-react";
import { FadeUp } from "@/components/anim";
import { buildReviewsJsonLd } from "@/components/Seo";
import { api } from "@/lib/api";
import { REVIEW_GOOGLE_URL } from "@/lib/site";

const Stars = ({ rating = 5, className = "" }) => (
  <div className={`flex items-center gap-0.5 ${className}`} aria-label={`${rating} out of 5 stars`}>
    {[...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${i < rating ? "text-[hsl(var(--kerrys-sun))] fill-[hsl(var(--kerrys-sun))]" : "text-muted-foreground/30"}`}
      />
    ))}
  </div>
);

const GoogleGlyph = () => (
  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5a5.6 5.6 0 0 1-2.4 3.6v3h3.9c2.3-2.1 3.5-5.2 3.5-8.8z" />
    <path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3c-1.1.7-2.5 1.2-4.1 1.2-3.1 0-5.8-2.1-6.7-5H1.3v3.1A12 12 0 0 0 12 24z" />
    <path fill="#FBBC05" d="M5.3 14.3a7.2 7.2 0 0 1 0-4.6V6.6H1.3a12 12 0 0 0 0 10.8l4-3.1z" />
    <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.5 1.8l3.4-3.4A12 12 0 0 0 12 0 12 12 0 0 0 1.3 6.6l4 3.1C6.2 6.9 8.9 4.8 12 4.8z" />
  </svg>
);

/**
 * Reusable reviews wall. Renders nothing until at least one review exists,
 * so the public site never shows an empty/placeholder state.
 * variant: "light" (default) | "dark"
 */
export const ReviewsWall = ({
  variant = "light",
  eyebrow = "What parents say",
  title = "Loved by Whitstable families",
  max = 6,
  withJsonLd = false,
  className = "",
}) => {
  const [reviews, setReviews] = useState(null);

  useEffect(() => {
    let active = true;
    api
      .get("/reviews")
      .then((res) => active && setReviews(res.data || []))
      .catch(() => active && setReviews([]));
    return () => {
      active = false;
    };
  }, []);

  if (!reviews || reviews.length === 0) return null;

  const shown = reviews.slice(0, max);
  const avg = reviews.reduce((s, r) => s + Number(r.rating || 0), 0) / reviews.length;
  const dark = variant === "dark";
  const jsonLd = withJsonLd ? buildReviewsJsonLd(reviews) : null;

  return (
    <section
      className={`grain ${dark ? "bg-[hsl(var(--kerrys-ink))] text-background" : "bg-secondary"} ${className}`}
      data-testid="reviews-wall"
    >
      {jsonLd && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        </Helmet>
      )}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <FadeUp className="max-w-2xl">
            <p className={`text-xs font-bold uppercase tracking-[0.3em] mb-4 ${dark ? "text-[hsl(var(--kerrys-sun))]" : "text-[hsl(var(--kerrys-sea))]"}`}>
              {eyebrow}
            </p>
            <h2 className={`font-display uppercase text-5xl sm:text-6xl leading-[0.92] ${dark ? "text-background" : ""}`}>{title}</h2>
          </FadeUp>
          <FadeUp delay={0.08}>
            <div className={`flex items-center gap-3 border-[0.5px] ${dark ? "border-background/30" : "border-foreground"} px-4 py-3`}>
              <GoogleGlyph />
              <div className="leading-none">
                <div className="flex items-center gap-2">
                  <span className="font-display text-2xl leading-none">{(Math.round(avg * 10) / 10).toFixed(1)}</span>
                  <Stars rating={Math.round(avg)} />
                </div>
                <p className={`text-[11px] mt-1.5 ${dark ? "text-background/60" : "text-muted-foreground"}`}>
                  from {reviews.length} Google review{reviews.length === 1 ? "" : "s"}
                </p>
              </div>
            </div>
          </FadeUp>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {shown.map((r, i) => (
            <FadeUp key={r.id} delay={(i % 3) * 0.06}>
              <figure
                className={`border-[0.5px] h-full p-6 sm:p-7 flex flex-col ${dark ? "border-background/25 bg-white/5" : "border-foreground bg-card"}`}
                data-testid="review-card"
              >
                <div className="flex items-center justify-between">
                  <Stars rating={r.rating} />
                  <Quote className={`h-5 w-5 ${dark ? "text-background/30" : "text-muted-foreground/40"}`} />
                </div>
                <blockquote className={`mt-4 leading-relaxed flex-1 ${dark ? "text-background/85" : "text-foreground/90"}`}>
                  &ldquo;{r.text}&rdquo;
                </blockquote>
                <figcaption className="mt-5 flex items-center gap-2.5">
                  <span
                    className={`h-9 w-9 shrink-0 flex items-center justify-center font-display text-lg border-[0.5px] ${
                      dark ? "border-background/30 bg-background/10" : "border-foreground bg-[hsl(var(--kerrys-sun-pale))]"
                    }`}
                    aria-hidden="true"
                  >
                    {r.author_name?.charAt(0).toUpperCase() || "?"}
                  </span>
                  <div className="min-w-0">
                    <p className="font-bold text-sm truncate">{r.author_name}</p>
                    <p className={`text-xs flex items-center gap-1.5 ${dark ? "text-background/55" : "text-muted-foreground"}`}>
                      <GoogleGlyph /> {r.source || "Google"}
                      {r.review_date ? ` · ${r.review_date}` : ""}
                    </p>
                  </div>
                </figcaption>
              </figure>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.1}>
          <div className="mt-10">
            <a
              href={REVIEW_GOOGLE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.12em] border-b-2 pb-1 transition-colors ${
                dark
                  ? "text-background border-[hsl(var(--kerrys-sun))] hover:text-[hsl(var(--kerrys-sun))]"
                  : "text-foreground border-[hsl(var(--kerrys-coral))] hover:text-[hsl(var(--kerrys-coral))]"
              }`}
              data-testid="reviews-google-link"
            >
              <GoogleGlyph /> Read more &amp; leave a review on Google
            </a>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};
